import { ref, type Ref, watch } from "vue";
import * as Cesium from "cesium";
import type { CameraEventType } from "cesium";
import type { ParsedTileConfig, RawTileConfig } from "@/utils/tileConfigParser";
import { parseTileConfig } from "@/utils/tileConfigParser";
import { backendFetch, type Sphere } from "@/utils/backendFetch";

type eventType = CameraEventType | any[] | undefined;

export function useFirstPersonMode(
  viewer: Ref<Cesium.Viewer | null>,
  currentSphere: Ref<Sphere | null>,
  showJakartoPointclouds: Ref<boolean>,
  toggleJakartoPointclouds: () => Promise<void>,
  showGoogle3DTiles: Ref<boolean>,
  toggleGoogle3DTiles: () => Promise<void>,
) {

  let isFirstPersonMode = ref<boolean>(false);
  const isFlyingToGround = ref<boolean>(false);

  // Parsed 2D tiles config for current sphere
  const sphereTileConfig = ref<ParsedTileConfig | null>(null);

  let currentEventTypes: {
    "translate": eventType,
    "zoom": eventType,
    "rotate": eventType,
    "tilt": eventType,
    "look": eventType,
  } = {
    "translate": undefined,
    "zoom": undefined,
    "rotate": undefined,
    "tilt": undefined,
    "look": undefined,
  }

  const lookAroundEventTypes = {
    "translate": undefined,
    "zoom": undefined,
    "rotate": undefined,
    "tilt": undefined,
    "look": [Cesium.CameraEventType.LEFT_DRAG],
  }

  const setFirstPersonMode = (value: boolean): void => {
    isFirstPersonMode.value = value;
    const ssc = viewer.value!.scene.screenSpaceCameraController;

    if (isFirstPersonMode.value) {
      currentEventTypes = {
        "translate": ssc.translateEventTypes,
        "zoom": ssc.zoomEventTypes,
        "rotate": ssc.rotateEventTypes,
        "tilt": ssc.tiltEventTypes,
        "look": ssc.lookEventTypes,
      }
      ssc.translateEventTypes = lookAroundEventTypes.translate;
      ssc.zoomEventTypes = lookAroundEventTypes.zoom;
      ssc.rotateEventTypes = lookAroundEventTypes.rotate;
      ssc.tiltEventTypes = lookAroundEventTypes.tilt;
      ssc.lookEventTypes = lookAroundEventTypes.look;
    } else {
      ssc.translateEventTypes = currentEventTypes.translate;
      ssc.zoomEventTypes = currentEventTypes.zoom;
      ssc.rotateEventTypes = currentEventTypes.rotate;
      ssc.tiltEventTypes = currentEventTypes.tilt;
      ssc.lookEventTypes = currentEventTypes.look;
    }
  };

  const enableFirstPersonMode = ({
    lon,
    lat,
  }: {
    lon: number;
    lat: number;
  }): void => {
    if (!viewer.value) return;

    // Set flag to temporarily disable minimum height enforcement
    isFlyingToGround.value = true;

    // backend fetch nearest sphere around lon, lat
    backendFetch(
      `/backend/spheres/?nearest_neighbors_to=${lon},${lat}&k=1&srid=4978&date__gte=2025-01-01`,
      {}
    )
      .then((response) => {
        return response.json();
      })
      .then((featureCollection) => {
        const camera = viewer.value!.camera;

        const feature = featureCollection.features[0];
        const props = feature.properties;
        const position = feature.geometry.coordinates;

        currentSphere.value = {
          image_id: props.image_id,
          date: new Date(props.date),
          x: position[0],
          y: position[1],
          z: position[2],
          heading: props.pan,
          pitch: props.tilt,
          roll: props.roll,
          z_offset: props.z_offset,
        };

        camera.flyTo({
          destination: Cesium.Cartesian3.fromArray(position),
          orientation: {
            heading: camera.heading,
            pitch: 0.0,
            roll: 0.0,
          },
          duration: 1.0,
          complete: async () => {
            if (!showJakartoPointclouds.value) {
              await toggleJakartoPointclouds();
            }
            if (!isFirstPersonMode.value) {
              setFirstPersonMode(true);
            }
          },
        });
      });
  };

  const disableFirstPersonMode = async (): Promise<void> => {
    if (!viewer.value) return;

    setFirstPersonMode(false);

    const camera = viewer.value.camera;
    const carto = Cesium.Cartographic.fromCartesian(camera.positionWC);
    const terrainHeight = viewer.value.scene.globe.getHeight(carto) ?? 0;
    const targetHeight = terrainHeight + 51;

    camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(
        Cesium.Math.toDegrees(carto.longitude),
        Cesium.Math.toDegrees(carto.latitude),
        targetHeight
      ),
      orientation: {
        heading: camera.heading,
        pitch: Cesium.Math.toRadians(-60.0),
        roll: 0.0,
      },
      duration: 1.0,
      complete: () => {
        isFlyingToGround.value = false;
      },
    });
  };

  const handleFirstPersonToggle = () => {
    if (isFirstPersonMode.value) {
      disableFirstPersonMode();
    } else {
      // Use current camera position as the target
      const camera = viewer.value?.camera;
      if (!camera) return;
      const carto = Cesium.Cartographic.fromCartesian(camera.positionWC);
      enableFirstPersonMode({
        lon: Cesium.Math.toDegrees(carto.longitude),
        lat: Cesium.Math.toDegrees(carto.latitude),
      });
    }
  };

  // Watch for sphere changes to fetch and parse tileset configuration
  watch(currentSphere, async (sphere) => {
    if (!sphere) {
      sphereTileConfig.value = null;
      return;
    }

    try {
      const response = await backendFetch(
        `/adapter/2dtiles/${sphere.image_id}.json?format=detailed`
      );
      const raw: RawTileConfig = await response.json();
      sphereTileConfig.value = parseTileConfig(raw);
      console.log("Parsed 2D tiles config", sphereTileConfig.value);
    } catch (error) {
      console.error("Failed to fetch/parse 2D tiles config:", error);
      sphereTileConfig.value = null;
    }
  });

  // React to sphereTileConfig changes while in first person mode
  watch(
    sphereTileConfig,
    (cfg) => {
      if (!viewer.value) return;
      viewer.value.scene.requestRender();
      if (cfg) {
        console.log("First person mode received new sphereTileConfig", cfg);
      }
    },
    { immediate: false }
  );

  return {
    isFirstPersonMode,
    isFlyingToGround,
    setFirstPersonMode,
    enableFirstPersonMode,
    disableFirstPersonMode,
    handleFirstPersonToggle,
  };
}
